import logger from "@/utils/logger";
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Button, Alert, Spinner } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import AppDialog from "@/components/ui/AppDialog";
import {
    UserApiService,
    Department,
} from "@/services/api/internal-login/internal-user-api-service";

interface DepartmentSelectionModalProps {
    show: boolean;
    organizationId: number;
    organizationName?: string;
    userEmail: string;
    onDepartmentSelected: (departmentId: number) => void;
    onError: (error: string) => void;
}

export default function DepartmentSelectionModal({
    show,
    organizationId,
    organizationName,
    userEmail,
    onDepartmentSelected,
    onError,
}: DepartmentSelectionModalProps) {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<
        Department[]
    >([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const userApiService = useMemo(() => new UserApiService(), []);

    // Wrap fetchDepartments with useCallback to fix dependency warning
    const fetchDepartments = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            logger.info(
                "Fetching departments for organization:",
                organizationId
            );
            const departmentList =
                await userApiService.getDepartmentsByOrganization(
                    organizationId
                );

            // Filter only active departments (your API should already filter this)
            const activeDepartments = departmentList.filter(
                (dept) => dept.isActive
            );
            setDepartments(activeDepartments);

            logger.info("Departments loaded:", activeDepartments);
        } catch (err: any) {
            const errorMessage = err.message || "Failed to load departments";
            setError(errorMessage);
            onError(errorMessage);
            console.error("Error fetching departments:", err);
        } finally {
            setLoading(false);
        }
    }, [organizationId, onError, userApiService]);

    // Fetch departments when modal opens
    useEffect(() => {
        if (show && organizationId) {
            fetchDepartments();
        }
    }, [show, organizationId, fetchDepartments]);

    const handleTypeaheadChange = (selected: Department[]) => {
        setSelectedDepartments(selected);
        setError(null);
    };

    // Pass userEmail to the API call
    const handleSubmit = async () => {
        const selectedDept = selectedDepartments[0];

        if (!selectedDept) {
            setError("Please select a department");
            return;
        }

        try {
            setSubmitting(true);
            setError(null);

            logger.info("Submitting department selection:", {
                selectedDepartmentId: selectedDept.departmentId,
                userEmail,
            });

            // Pass both departmentId and userEmail
            await userApiService.updateUserDepartment(
                selectedDept.departmentId,
                userEmail
            );

            // Notify parent component
            onDepartmentSelected(selectedDept.departmentId);

            logger.info("Department selection completed successfully");
        } catch (err: any) {
            const errorMessage = err.message || "Failed to update department";
            setError(errorMessage);
            onError(errorMessage);
            console.error("Error updating department:", err);
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        // Allow closing only if not submitting
        if (!submitting) {
            onError("Department selection is required to continue");
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="text-center py-4">
                    <Spinner
                        animation="border"
                        variant="primary"
                        className="mb-2"
                    />
                    <p className="text-muted mb-0 small">
                        Loading departments...
                    </p>
                </div>
            );
        }

        if (error && departments.length === 0) {
            return (
                <div className="py-2">
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <span>{error}</span>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={fetchDepartments}
                            >
                                Retry
                            </Button>
                        </div>
                    </Alert>
                </div>
            );
        }

        return (
            <div className="modal-content-wrapper">
                {/* Main Content */}
                <div className="text-center mb-4">
                    <p
                        className="mb-1 dept-modal-text"
                        style={{
                            fontSize: "14px",
                            lineHeight: "1.3",
                        }}
                    >
                        Dispatch-IQ requires your current department.
                        <br />
                        Please confirm your department at
                        <br />
                        <strong>
                            {organizationName || "your organization"}
                        </strong>
                        .
                    </p>
                </div>

                <div className="text-center mb-4">
                    <p className="text-muted mb-0" style={{ fontSize: "12px" }}>
                        You can modify this from your profile.
                    </p>
                </div>

                {/* Error Alert */}
                {error && (
                    <Alert
                        variant="warning"
                        className="mb-3"
                        dismissible
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Searchable Department Selection using react-bootstrap-typeahead */}
                <div className="mb-4">
                    <Typeahead
                        id="department-selection-typeahead"
                        labelKey="departmentName"
                        options={departments}
                        placeholder="Search and select a department..."
                        selected={selectedDepartments}
                        onChange={(selected: any[]) =>
                            handleTypeaheadChange(selected as Department[])
                        }
                        disabled={submitting}
                        clearButton={true}
                        highlightOnlyResult
                        emptyLabel="No departments found."
                        className="department-typeahead"
                        inputProps={{
                            style: {
                                padding: "12px 16px",
                                fontSize: "14px",
                                border: "1px solid #ddd",
                                borderRadius: "6px",
                            },
                        }}
                        // menuProps removed because it's not a valid prop for Typeahead
                        renderMenuItemChildren={(option: any) => (
                            <div>
                                <strong>{option.departmentName}</strong>
                                {option.description && (
                                    <div>
                                        <small className="text-muted">
                                            {option.description}
                                        </small>
                                    </div>
                                )}
                            </div>
                        )}
                    />

                    {/* Department count indicator */}
                    {departments.length > 0 && (
                        <small className="text-muted mt-2 d-block">
                            <i className="bi bi-info-circle me-1"></i>
                            {departments.length} departments available
                        </small>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <AppDialog
                show={show}
                onHide={handleClose}
                title="Select Department"
                size="md"
                footer={
                    <div className="d-flex justify-content-end gap-2 w-100">
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={
                                submitting || selectedDepartments.length === 0
                            }
                            style={{
                                borderRadius: "6px",
                                padding: "8px 20px",
                                fontSize: "14px",
                            }}
                        >
                            {submitting ? (
                                <>
                                    <Spinner
                                        animation="border"
                                        size="sm"
                                        className="me-2"
                                    />
                                    Confirming...
                                </>
                            ) : (
                                "Confirm"
                            )}
                        </Button>
                    </div>
                }
            >
                <div style={{ padding: "24px" }}>{renderContent()}</div>
            </AppDialog>

            {/* Custom CSS for better integration - Same as DepartmentEditModal */}
            <style jsx global>{`
                /* Typeahead custom styling for better Bootstrap integration */
                .department-typeahead .rbt-input-main {
                    background-color: #fff;
                    border: 1px solid #ddd !important;
                    border-radius: 6px !important;
                    padding: 12px 16px !important;
                    font-size: 14px !important;
                    line-height: 1.5;
                    transition:
                        border-color 0.15s ease-in-out,
                        box-shadow 0.15s ease-in-out;
                }

                .department-typeahead .rbt-input-main:focus {
                    border-color: #86b7fe !important;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25) !important;
                    outline: 0 !important;
                }

                .department-typeahead .rbt-menu {
                    border: 1px solid #ddd !important;
                    border-radius: 6px !important;
                    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
                    margin-top: 4px;
                }

                .department-typeahead .rbt-menu-item {
                    padding: 8px 16px !important;
                    font-size: 14px !important;
                    border-bottom: 1px solid #f8f9fa;
                }

                .department-typeahead .rbt-menu-item:hover,
                .department-typeahead .rbt-menu-item.active {
                    background-color: #f8f9fa !important;
                    color: #333 !important;
                }

                .department-typeahead .rbt-menu-item:last-child {
                    border-bottom: none;
                }

                .department-typeahead .rbt-close {
                    color: #6c757d !important;
                    font-size: 14px !important;
                    background-color: #f8f9fa !important;
                    border-radius: 50% !important;
                    width: 20px !important;
                    height: 20px !important;
                    display: flex !important;
                    align-items: center !important;
                    justify-content: center !important;
                    margin-right: 8px !important;
                }

                .department-typeahead .rbt-close:hover {
                    color: #fff !important;
                    background-color: #6c757d !important;
                }

                /* Dark mode support */
                html[data-bs-theme="dark"]
                    .department-typeahead
                    .rbt-input-main,
                body[data-bs-theme="dark"]
                    .department-typeahead
                    .rbt-input-main {
                    background-color: #2b2f33 !important;
                    border-color: #495057 !important;
                    color: #f8f9fa !important;
                }

                html[data-bs-theme="dark"] .department-typeahead .rbt-menu,
                body[data-bs-theme="dark"] .department-typeahead .rbt-menu {
                    background-color: #2b2f33 !important;
                    border-color: #495057 !important;
                }

                html[data-bs-theme="dark"] .department-typeahead .rbt-menu-item,
                body[data-bs-theme="dark"]
                    .department-typeahead
                    .rbt-menu-item {
                    color: #f8f9fa !important;
                    border-bottom-color: #495057 !important;
                }

                html[data-bs-theme="dark"]
                    .department-typeahead
                    .rbt-menu-item:hover,
                html[data-bs-theme="dark"]
                    .department-typeahead
                    .rbt-menu-item.active,
                body[data-bs-theme="dark"]
                    .department-typeahead
                    .rbt-menu-item:hover,
                body[data-bs-theme="dark"]
                    .department-typeahead
                    .rbt-menu-item.active {
                    background-color: #495057 !important;
                }

                /* Loading and empty states */
                .department-typeahead .rbt-menu .dropdown-item {
                    padding: 8px 16px;
                    font-size: 14px;
                }
            `}</style>
        </>
    );
}
